import React, { useEffect, useState, forwardRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import classNames from "classnames";
import { isNull } from "lodash";
import { useTranslation } from "react-i18next";
import { Input } from "@client.components/Modal";
import {
  getAvailableRolesAsync,
  getSalaryRangesAsync,
  getTeamsAsync,
  makeUserManagementError,
} from "@client.pages/Settings/reducer";
import DropdownUserRolesComponent from "./DropdownUserRoles";
import Loader from "@client.components/Loader";
import UserRoles from "@client.enums/userRoles";
import ValidationError from "@client.components/ValidationError";
import { makeProfile } from "@client.pages/Login/reducer";
import DropdownSalaryRange from "./DropdownSalaryRange";
import DropdownTeams from "./DropdownTeams";
import UserModel from "@client.models/user";
import "./UserManagementContent.scss";
import { FormCheckbox } from "@client.components/Checkbox";

export const UserManagementContent = forwardRef(({ user = {} }, ref) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const issuer = useSelector(makeProfile);

  const [userToSave, setUserToSave] = useState(
    new UserModel(
      user,
      [
        "email",
        "firstName",
        "lastName",
        "role",
        "teams",
        "salaryRange",
        "invalidEmail",
        "invalidRole",
        "invalidDomain",
      ].reduce((dict, key) => {
        dict[key] = t(`components.UserManagementContent.validation.${key}`);
        return dict;
      }, {}),
      issuer
    )
  );

  const [validation, setValidation] = useState({});
  const error = useSelector(makeUserManagementError);

  useEffect(() => {
    if (userToSave.id) {
      const msgs = userToSave.validate();
      setValidation(msgs);
    }
  }, [userToSave]);

  useEffect(() => {
    Promise.all([
      dispatch(getSalaryRangesAsync()),
      dispatch(getTeamsAsync()),
      dispatch(getAvailableRolesAsync()),
    ]).then(() => {
      setLoading(false);
    });
  }, [dispatch]);

  React.useImperativeHandle(ref, () => ({
    managementItem: userToSave,
    validate: () => {
      const msgs = userToSave.validate();
      setValidation(msgs);
      return Object.keys(msgs).length === 0;
    },
  }));

  const updateUserModel = (field, value) => {
    setUserToSave(
      new UserModel(
        { ...userToSave, [field]: value },
        userToSave.validationMsgs,
        userToSave.issuer
      )
    );
  };

  return (
    <div className={classNames("user-mngm-content", { loading: loading })}>
      {(loading || isNull(userToSave)) && <Loader loading={true} />}
      {!isNull(userToSave) && !loading && (
        <>
          <Input
            value={userToSave.firstName}
            label="components.UserManagementContent.firstName"
            placeholder="components.UserManagementContent.placeholder.firstName"
            onChange={(v) => updateUserModel("firstName", v)}
            error={validation.firstName}
          />
          <Input
            value={userToSave.lastName}
            label="components.UserManagementContent.lastName"
            placeholder="components.UserManagementContent.placeholder.lastName"
            onChange={(v) => updateUserModel("lastName", v)}
            error={validation.lastName}
          />
          <Input
            disabled={!!userToSave.id}
            value={userToSave.email}
            label="components.UserManagementContent.email"
            placeholder="components.UserManagementContent.placeholder.email"
            onChange={(v) => updateUserModel("email", v)}
            error={validation.email}
          />
          <DropdownUserRolesComponent
            error={validation.role}
            disabled={issuer.userId === userToSave.id}
            selectedRole={userToSave.role}
            onSelect={(v) => {
              setUserToSave(
                new UserModel(
                  {
                    ...userToSave,
                    role: v,
                    teams: v === UserRoles.Viewer ? [] : userToSave.teams,
                  },
                  userToSave.validationMsgs,
                  userToSave.issuer
                )
              );
            }}
          />
          {userToSave.role !== UserRoles.Viewer && (
            <DropdownTeams
              error={validation.teams}
              value={userToSave.teams}
              onSelect={(items) => updateUserModel("teams", items)}
            />
          )}
          <DropdownSalaryRange
            error={validation.salaryRangeId}
            selectedSalaryRangeId={userToSave.salaryRangeId}
            onSelect={(v) => {
              updateUserModel("salaryRangeId", v);
            }}
          />
          {!user.id && (
            <FormCheckbox
              label={"components.UserManagementContent.sendInvite"}
              value={userToSave.sendInvite}
              onChange={(v) => {
                updateUserModel("sendInvite", v);
              }}
            />
          )}
        </>
      )}
      {!isNull(error) && (
        <ValidationError className="full-row" error={[t(error)]} />
      )}
    </div>
  );
});
UserManagementContent.displayName = "UserManagementContent";

UserManagementContent.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    role: PropTypes.string,
    teams: PropTypes.array,
  }),
};
